import { useMemo, useState } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import './stream.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatMoney(value) {
  return `£${Number(value || 0).toFixed(2)}`;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function StreamTable({ streamData }) {
  const [sortBy, setSortBy] = useState('revenue');
  const [filterBy, setFilterBy] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredProducts = useMemo(() => {
    if (!streamData?.products) return [];

    return streamData.products
      .filter((product) => {
        if (searchTerm && !product.name.toLowerCase().includes(searchTerm.toLowerCase())) {
          return false;
        }

        switch (filterBy) {
          case 'globalScheme':
            return product.isGlobalScheme;
          case 'standard':
            return !product.isGlobalScheme;
          case 'shipped':
            return !product.cancelled;
          case 'cancelled':
            return product.cancelled;
          default:
            return true;
        }
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'revenue':
            return b.totalRevenue - a.totalRevenue;
          case 'profit':
            return b.netProfit - a.netProfit;
          case 'margin':
            return (b.profitMargin || -Infinity) - (a.profitMargin || -Infinity);
          case 'quantity':
            return b.quantity - a.quantity;
          default:
            return 0;
        }
      });
  }, [filterBy, searchTerm, sortBy, streamData]);

  if (!streamData) return null;

  return (
    <div className="srv-sales">
      {/* Controls card */}
      <section className="rc-panel srv-controls">
        <div className="srv-controls__head">
          <div className="srv-heading">
            <h2 className="srv-title">
              Sales Data Table
            </h2>
            <p className="srv-sub">
              Sortable table with brand-styled dark rows and hover states.
            </p>
          </div>
          <div className="rc-input srv-search">
            <MagnifyingGlass size={16} aria-hidden="true" />
            <input
              type="text"
              placeholder="Search products"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="srv-chips">
          {[
            ['all', 'All'],
            ['globalScheme', 'Global Scheme'],
            ['standard', 'Standard VAT'],
            ['shipped', 'Shipped'],
            ['cancelled', 'Cancelled'],
          ].map(([value, label]) => (
            <button
              key={value}
              className={filterBy === value ? 'rc-chip is-active' : 'rc-chip'}
              onClick={() => setFilterBy(value)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="rc-segmented srv-sort">
          {[
            ['revenue', 'Sort by Revenue'],
            ['profit', 'Sort by Profit'],
            ['margin', 'Sort by Margin %'],
            ['quantity', 'Sort by Quantity'],
          ].map(([value, label]) => (
            <button
              key={value}
              className={sortBy === value ? 'is-active' : undefined}
              onClick={() => setSortBy(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </section>

      {/* Table */}
      {filteredProducts.length > 0 ? (
        <section className="rc-panel rc-panel--flush srv-table-panel">
          <div className="rc-table-wrap">
            <table className="rc-table rc-table--stack srv-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>SKU</th>
                  <th className="rc-num">Orders</th>
                  <th className="rc-num">Items</th>
                  <th className="rc-num">Revenue</th>
                  <th className="rc-num">Cost</th>
                  <th className="rc-num">Fees</th>
                  <th className="rc-num">VAT</th>
                  <th className="rc-num">Net Profit</th>
                  <th className="rc-num">Margin</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => (
                  <tr key={`${product.name}-${product.sku || 'no-sku'}`}>
                    <td className="srv-product">
                      <div className="srv-product__name">
                        {product.name}
                      </div>
                      <div className="srv-product__meta" data-scheme={product.isGlobalScheme ? 'global' : undefined}>
                        {product.isGlobalScheme ? 'Global Scheme' : 'Standard VAT'}
                        {product.variantCount > 1 ? ` • ${product.variantCount} variants` : ''}
                      </div>
                    </td>
                    <td className="srv-sku" data-label="SKU">{product.sku || '—'}</td>
                    <td className="rc-num" data-label="Orders">{product.quantity}</td>
                    <td className="rc-num" data-label="Items">{product.totalItems}</td>
                    <td className="rc-num" data-label="Revenue">{formatMoney(product.totalRevenue)}</td>
                    <td className="rc-num" data-label="Cost">{product.hasCostPrice ? formatMoney(product.totalCost) : '—'}</td>
                    <td className="rc-num" data-label="Fees">{formatMoney(product.totalFees)}</td>
                    <td className="rc-num" data-label="VAT">{formatMoney(product.totalVAT)}</td>
                    <td className="rc-num" data-label="Net Profit" data-loss={product.hasCostPrice && product.netProfit < 0 ? 'true' : undefined}>
                      {product.hasCostPrice ? formatMoney(product.netProfit) : '—'}
                    </td>
                    <td className="rc-num" data-label="Margin" data-loss={product.profitMargin !== null && product.profitMargin < 0 ? 'true' : undefined}>
                      {product.profitMargin !== null ? `${product.profitMargin.toFixed(1)}%` : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <div className="rc-empty srv-empty">
          <p className="rc-empty__body">No products match the current filters.</p>
        </div>
      )}

      {/* Profit breakdown */}
      <section className="rc-panel srv-card">
        <h2 className="srv-card__title">
          Profit Calculation Breakdown
        </h2>
        <div className="srv-rows">
          {[
            ['Total Revenue (all items)', formatMoney(streamData.totalRevenue)],
            ['Revenue (items with cost only)', formatMoney(streamData.revenueWithCost)],
            ['Total Cost', `-${formatMoney(streamData.totalCost)}`],
            ['Gross Profit (items with cost)', formatMoney(streamData.grossProfit)],
            ['Whatnot Fees (items with cost)', `-${formatMoney(streamData.feesWithCost)}`],
            ['VAT (items with cost)', `-${formatMoney(streamData.vatWithCost)}`],
            ['Net Profit (items with cost)', formatMoney(streamData.netProfit)],
            ['Profit Margin', streamData.profitMargin ? `${streamData.profitMargin.toFixed(1)}%` : 'N/A'],
          ].map(([label, value]) => (
            <div key={label} className="srv-row">
              <span className="srv-row__label">{label}</span>
              <span className="srv-row__value">{value}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
